import { Pencil, MapPin, CalendarDays, Truck, ClipboardList, AlertCircle } from "lucide-react";
import BidDeadlinePicker from "@/components/lr-direct/BidDeadlinePicker";
import { EQUIPMENT_FLAT_LIST } from "@/lib/equipmentTypes";
import { formatYYYYMMDDToLocaleDateString } from "@/lib/dateOnly";
import { pickupLabel, dropLabel, type WizardFields } from "./wizardTypes";

/**
 * Guided wizard — STEP 6: REVIEW & POST.
 *
 * Full summary of everything entered, with Edit links that jump back to any
 * step (state is preserved). The bid/quote deadline is auto-derived from the
 * pickup date (11:59 PM default, enforced ≥30 min in the future) and editable
 * here — same BidDeadlinePicker the form's publish card uses.
 */

interface WizardReviewStepProps {
  fields: WizardFields;
  postType: "load" | "quote";
  onEditStep: (step: number) => void;
  onBidDeadlineChange: (v: string) => void;
  error?: string | null;
}

function Section({
  title,
  Icon,
  editStep,
  onEditStep,
  children,
}: {
  title: string;
  Icon: typeof MapPin;
  editStep: number;
  onEditStep: (step: number) => void;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-xl border border-border bg-card p-4 space-y-2" style={{ boxShadow: "var(--shadow-card)" }}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <Icon className="h-3.5 w-3.5" style={{ color: "hsl(var(--brand-teal-deep))" }} />
          <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{title}</span>
        </div>
        <button
          type="button"
          onClick={() => onEditStep(editStep)}
          className="inline-flex items-center gap-1 text-[10px] font-semibold transition-colors"
          style={{ color: "hsl(var(--brand-teal-deep))" }}
        >
          <Pencil className="h-3 w-3" />
          Edit
        </button>
      </div>
      {children}
    </div>
  );
}

const fmtDate = (ymd: string) =>
  ymd ? formatYYYYMMDDToLocaleDateString(ymd, "en-US", { month: "short", day: "numeric", year: "numeric" }) : "—";

export default function WizardReviewStep({
  fields,
  postType,
  onEditStep,
  onBidDeadlineChange,
  error,
}: WizardReviewStepProps) {
  const equipmentLabel =
    EQUIPMENT_FLAT_LIST.find((t) => t.enumValue === fields.equipmentType)?.name || fields.equipmentType || "—";

  const weightNum = parseInt(fields.weight, 10);
  let pickupIdx = 0;
  let dropIdx = 0;

  const extras: [string, string][] = [];
  if (fields.commodity) extras.push(["Commodity", fields.commodity]);
  if (fields.referenceNumber) extras.push(["Reference #", fields.referenceNumber]);
  if (fields.comments) extras.push(["Comments", fields.comments]);
  if (fields.handlingRequirements.length > 0) extras.push(["Handling", fields.handlingRequirements.join(", ")]);
  if (fields.shipperNotes) extras.push(["Note to brokers", fields.shipperNotes]);

  return (
    <div className="space-y-3">
      {error && (
        <div className="flex items-start gap-2 rounded-lg border border-destructive/40 bg-destructive/5 px-3 py-2.5">
          <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5 text-destructive" />
          <p className="text-xs text-destructive">{error}</p>
        </div>
      )}

      {/* Route */}
      <Section title="Route" Icon={MapPin} editStep={2} onEditStep={onEditStep}>
        <ul className="space-y-1.5">
          {fields.stops.map((stop) => {
            const isPickup = stop.stop_type === "pickup";
            const label = isPickup
              ? pickupLabel(pickupIdx++, fields.pickupCount)
              : dropLabel(dropIdx++, fields.dropCount);
            return (
              <li key={stop.id} className="flex items-center justify-between gap-2 text-xs">
                <span className="flex items-center gap-2 min-w-0">
                  <span
                    className="flex-shrink-0 inline-flex items-center justify-center h-4 w-4 rounded text-[8px] font-bold uppercase"
                    style={
                      isPickup
                        ? { backgroundColor: "hsl(var(--brand-teal-deep) / 0.12)", color: "hsl(var(--brand-teal-deep))" }
                        : { backgroundColor: "hsl(var(--destructive) / 0.1)", color: "hsl(var(--destructive))" }
                    }
                  >
                    {isPickup ? "P" : "D"}
                  </span>
                  <span className="font-medium text-foreground truncate">
                    {stop.city && stop.state ? `${stop.city}, ${stop.state}` : "—"}
                  </span>
                </span>
                <span className="text-muted-foreground flex-shrink-0">{fmtDate(stop.scheduled_date)}</span>
              </li>
            );
          })}
        </ul>
      </Section>

      {/* Dates */}
      <Section title="Dates" Icon={CalendarDays} editStep={3} onEditStep={onEditStep}>
        <div className="grid grid-cols-2 gap-2 text-xs">
          <div>
            <p className="text-[10px] text-muted-foreground">Pickup</p>
            <p className="font-medium text-foreground">{fmtDate(fields.pickupDate)}</p>
          </div>
          <div>
            <p className="text-[10px] text-muted-foreground">Delivery</p>
            <p className="font-medium text-foreground">
              {fields.deliveryDate ? fmtDate(fields.deliveryDate) : postType === "quote" ? "Not set (optional)" : "—"}
            </p>
          </div>
        </div>
      </Section>

      {/* Equipment & weight */}
      <Section title="Equipment & Weight" Icon={Truck} editStep={4} onEditStep={onEditStep}>
        <div className="text-xs space-y-1">
          <p className="font-medium text-foreground">{equipmentLabel}</p>
          <p className="text-muted-foreground">
            {fields.loadType === "FTL" ? "Full (FTL)" : "Partial (LTL)"}
            {" · "}
            {fields.loadNature.charAt(0).toUpperCase() + fields.loadNature.slice(1)}
            {!isNaN(weightNum) && weightNum > 0 && <>{" · "}{weightNum.toLocaleString()} lbs</>}
            {fields.length && <>{" · "}{fields.length} ft</>}
          </p>
        </div>
      </Section>

      {/* Extras */}
      {extras.length > 0 && (
        <Section title="Extras" Icon={ClipboardList} editStep={5} onEditStep={onEditStep}>
          <dl className="text-xs space-y-1">
            {extras.map(([k, v]) => (
              <div key={k} className="flex items-start gap-2">
                <dt className="w-24 flex-shrink-0 text-muted-foreground">{k}</dt>
                <dd className="font-medium text-foreground break-words min-w-0">{v}</dd>
              </div>
            ))}
          </dl>
        </Section>
      )}

      {/* Deadline — auto-derived, editable */}
      <div
        className="rounded-xl border p-4 space-y-2"
        style={{
          borderColor: "hsl(var(--accent-private) / 0.55)",
          backgroundColor: "hsl(var(--accent-private) / 0.03)",
        }}
      >
        <span
          className="text-[10px] font-bold uppercase tracking-wider"
          style={{ color: "hsl(var(--brand-teal-deep))" }}
        >
          {postType === "quote" ? "Quote deadline" : "Bid deadline"}
        </span>
        <BidDeadlinePicker value={fields.bidDeadline} onChange={onBidDeadlineChange} />
      </div>
    </div>
  );
}
